import { ReactElement, useCallback, useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { Booking, BookingCompressed, ResourceCompressed } from '@jooxter/api';
import { IRange, toISO } from '@jooxter/utils';
import { canDeleteBooking, canUpdateBooking, defaultActionFor, partitionOverlapActions } from './helpers';
import { OverlapResolutionActionEnum } from './types';
import { useBookingCompressedToBookingRequestAdapter } from './adapter';
import { useDeleteBookings, useUpdateBooking } from '../mutations';
import { useFetchBooking } from '../queries/queryFns';
import JxtOverlapResolutionModal from './JxtOverlapResolutionModal';

export const useOverlapResolution = (
  overlappingBookings: BookingCompressed[],
  // Both required for the "Move" choice to be offered - omit either to only offer Delete/Truncate
  // (e.g. the scheduler drag-and-drop flow, where there's no sensible third resource to move onto).
  newResource: ResourceCompressed | undefined,
  // Called once the user confirms their delete/truncate choices, with a `commit` function that
  // actually sends those mutations. The caller decides *when* to invoke `commit` - e.g. the
  // booking-form flow calls it immediately since there's no further confirmation step, while the
  // scheduler drag flow defers it until its own recurrence-confirmation modal is also confirmed, so
  // that cancelling that later step never leaves an already-deleted/truncated booking behind.
  onResolved: (commit: () => Promise<void>) => void,
  onMove: ((booking: Booking, resource: ResourceCompressed) => void) | undefined,
  onCancel?: () => void,
  // Injectable for tests; defaults to the real current time.
  now: DateTime = DateTime.now()
): { openOverlapResolution: (newRange: IRange) => void; overlapResolutionModal: ReactElement | null } => {
  const allowMove = !!newResource && !!onMove;
  const [show, setShow] = useState<boolean>(false);
  const [newRange, setNewRange] = useState<IRange>();
  const [actions, setActions] = useState<Map<number, OverlapResolutionActionEnum>>(new Map());
  const [moveTargetId, setMoveTargetId] = useState<number>();

  const { adapt, isLoading: isAdapterLoading } = useBookingCompressedToBookingRequestAdapter(overlappingBookings);
  const { mutateAsync: deleteBookingsAsync } = useDeleteBookings();
  const { mutateAsync: updateBookingAsync } = useUpdateBooking();
  const { booking: movingBooking } = useFetchBooking(moveTargetId);

  useEffect(() => {
    if (moveTargetId && movingBooking && onMove && newResource) {
      setShow(false);
      setMoveTargetId(undefined);
      onMove(movingBooking, newResource);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movingBooking, moveTargetId]);

  // Computed here rather than in an effect keyed on `overlappingBookings`, because only at this point
  // - once `range` is known - can Truncate's own time-based viability (see computeTruncatedRange) be
  // factored into the default, alongside Delete/Move's capability-based checks. Otherwise a booking
  // that can only be Moved (e.g. Delete forbidden by capability, Truncate ruled out by time) would end
  // up defaulting to a disabled Truncate radio instead of the one choice that's actually selectable.
  const openOverlapResolution = useCallback(
    (range: IRange) => {
      setNewRange(range);
      setActions(new Map(overlappingBookings.map((b) => [b.id, defaultActionFor(b, range, now, allowMove)])));
      setShow(true);
    },
    [overlappingBookings, now, allowMove]
  );

  const onHide = useCallback(() => {
    setShow(false);
    onCancel?.();
  }, [onCancel]);

  const onConfirm = useCallback(() => {
    if (!newRange) {
      return;
    }

    const { toDelete, toTruncate, toMove } = partitionOverlapActions(overlappingBookings, actions, newRange, now);

    const commit = () =>
      Promise.all([
        toDelete.length ? deleteBookingsAsync(toDelete) : Promise.resolve(),
        ...toTruncate.map(({ booking, range }) => {
          const payload = adapt(booking);

          if (!payload) {
            return Promise.resolve();
          }

          return updateBookingAsync({
            id: booking.id,
            payload: { ...payload, start: { dateTime: toISO(range.from) }, end: { dateTime: toISO(range.to) } },
          });
        }),
      ]).then(() => undefined);

    if (toMove && allowMove) {
      // The move pivots the caller straight into an automatic follow-up (fetching then editing the
      // moved booking) with no further user-cancellable step, so it's safe to commit the other
      // bookings' delete/truncate choices right away here rather than deferring them. The mutations
      // themselves already report failures (via their own onError), so on rejection just close the
      // modal rather than leaving it stuck open with no further feedback.
      commit()
        .then(() => setMoveTargetId(toMove.id))
        .catch(() => setShow(false));
      return;
    }

    setShow(false);
    onResolved(commit);
  }, [
    newRange,
    overlappingBookings,
    actions,
    adapt,
    deleteBookingsAsync,
    updateBookingAsync,
    onResolved,
    allowMove,
    now,
  ]);

  // Only Truncate actually depends on the adapter's fetch (to build the update payload) - gating
  // Confirm on `isAdapterLoading` regardless of the chosen actions would needlessly block the common
  // Delete-only case behind fetches it doesn't need.
  const hasUnresolvedTruncate =
    isAdapterLoading && [...actions.values()].some((action) => action === OverlapResolutionActionEnum.Truncate);

  // None of Delete/Truncate/Move can ever be committed for a booking the principal can neither
  // cancel nor update - Confirm is disabled entirely rather than letting the user "resolve" it with
  // a choice that partitionOverlapActions would just have to silently drop.
  const hasUnresolvableBooking = overlappingBookings.some((b) => !canDeleteBooking(b) && !canUpdateBooking(b));

  const overlapResolutionModal = newRange ? (
    <JxtOverlapResolutionModal
      show={show}
      bookings={overlappingBookings}
      actions={actions}
      newResource={newResource}
      allowMove={allowMove}
      newRange={newRange}
      onChange={setActions}
      onConfirm={onConfirm}
      onHide={onHide}
      isLoading={hasUnresolvedTruncate}
      hasUnresolvableBooking={hasUnresolvableBooking}
      now={now}
    />
  ) : null;

  return { openOverlapResolution, overlapResolutionModal };
};
